"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function IgniteProject() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const [isHovered, setIsHovered] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let width = 0;
    let height = 0;
    const resize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
    };
    resize();
    window.addEventListener("resize", resize);
    interface Streak {
      y: number;
      x: number;
      length: number;
      speed: number;
      opacity: number;
      width: number;
      color: string;
    }
    const streaks: Streak[] = [];
    const colors = ["rgba(255, 191, 0,", "rgba(255, 255, 255,", "rgba(255, 191, 0,", "rgba(200, 150, 0,"];
    for (let i = 0; i < 18; i++) {
      streaks.push({
        y: Math.random() * 100,
        x: Math.random() * 100,
        length: 80 + Math.random() * 200,
        speed: 0.03 + Math.random() * 0.06,
        opacity: 0.04 + Math.random() * 0.18,
        width: 0.5 + Math.random() * 1.5,
        color: colors[Math.floor(Math.random() * colors.length)]
      });
    }
    let running = true;
    const animate = () => {
      if (!running) return;
      if (width === 0 || height === 0) {
        animFrameRef.current = requestAnimationFrame(animate);
        return;
      }
      ctx.clearRect(0, 0, width, height);
      for (const streak of streaks) {
        streak.x += streak.speed;
        if (streak.x > 110) {
          streak.x = -20;
          streak.y = Math.random() * 100;
          streak.opacity = 0.04 + Math.random() * 0.18;
        }
        const xPx = streak.x / 100 * width;
        const yPx = streak.y / 100 * height;
        const grad = ctx.createLinearGradient(xPx - streak.length, yPx, xPx, yPx);
        grad.addColorStop(0, `${streak.color}0)`);
        grad.addColorStop(0.5, `${streak.color}${streak.opacity})`);
        grad.addColorStop(1, `${streak.color}0)`);
        ctx.beginPath();
        ctx.moveTo(xPx - streak.length, yPx);
        ctx.lineTo(xPx, yPx);
        ctx.strokeStyle = grad;
        ctx.lineWidth = streak.width;
        ctx.stroke();
      }
      animFrameRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      running = false;
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <section data-section-id="232408" id="the-shield-of-trust" className="dark bg-background text-foreground relative overflow-hidden">
      {}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{
      zIndex: 1
    }} />

      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(255,191,0,0.05) 0%, transparent 70%)",
      zIndex: 2
    }} />

      {}
      <div className="absolute inset-x-0 top-0 h-32 pointer-events-none" style={{
      background: "linear-gradient(to bottom, var(--background), transparent)",
      zIndex: 2
    }} />
      <div className="absolute inset-x-0 bottom-0 h-32 pointer-events-none" style={{
      background: "linear-gradient(to top, var(--background), transparent)",
      zIndex: 2
    }} />

      {}
      <div ref={sectionRef} className="relative flex flex-col items-center justify-center text-center max-w-4xl ![padding:192px_0px_192px_0px] ![margin:0_auto]" style={{
      zIndex: 3
    }}>
        {}
        <div className="flex justify-center w-full mb-5">
          <img src="https://peaceriverpest.com/wp-content/uploads/2026/06/Subject-1.png" alt="Peace River Pest Control Logo" style={{
          maxWidth: "240px",
          height: "auto",
          marginBottom: "20px"
        }} className="!w-[240px] !h-[100px] ![margin:0px_0px_60px_0px]" />
        </div>

        {}
        <p className={`font-default text-xs tracking-widest uppercase mb-6 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        color: "var(--primary)",
        transitionDelay: "0ms"
      }}>
          Secure Your Home with Professional Pest Control, Termite Treatment, Mosquito Control, and Rodent Control in Punta Gorda, FL
        </p>

        {}
        <h2 className={`font-default text-5xl md:text-7xl lg:text-[42px] font-bold uppercase tracking-tight mb-10 leading-tight transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
        color: "var(--foreground)",
        transitionDelay: "100ms"
      }}>READY TO PROTECT YOUR PUNTA GORDA HOME FROM PESTS, TERMITES, MOSQUITOES, SPIDERS, AND RODENTS?</h2>

        {}
        <div className={`w-24 h-1 mb-10 transition-all duration-700 ${isVisible ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"}`} style={{
        background: "var(--primary)",
        transitionDelay: "200ms"
      }} />

        {}
        <div className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        transitionDelay: "300ms"
      }}>
          <Link to="/ContactPage">
            <Button asChild size="lg" className="relative overflow-hidden px-10 py-6 h-auto text-base font-default font-bold uppercase tracking-widest rounded-lg transition-all duration-300 shadow-md" style={{
            background: isHovered ? "var(--foreground)" : "var(--primary)",
            color: isHovered ? "var(--background)" : "var(--primary-foreground)",
            border: `2px solid ${isHovered ? "var(--foreground)" : "var(--primary)"}`,
            transform: isHovered ? "scale(1.05)" : "scale(1)"
          }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
              <span className="relative z-10 flex items-center gap-3">
                Get A Free Quote
                <ArrowRight className="transition-transform duration-300" style={{
                transform: isHovered ? "translateX(6px)" : "translateX(0px)"
              }} size={18} />
              </span>
            </Button>
          </Link>
        </div>

        {}
        <p className={`font-serif text-sm md:text-2xl mt-8 max-w-2xl leading-relaxed transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        color: "var(--muted-foreground)",
        transitionDelay: "420ms"
      }}>As a leading choice for Pest Control Punta Gorda FL, we provide expert Pest and Termite Treatments in Punta Gorda FL, as well as Mosquito Control, Spider Control and Rodent Control. Our professional team is dedicated to protecting homeowners across Punta Gorda, Cape Coral, Burnt Store, Deep Creek, North Fort Myers, Arcadia, and all of Charlotte, Lee, and Sarasota Counties. Let us build a pest-free environment for your family with reliable, local expertise you can trust.</p>

        {}
        <Link to="/ContactPage" className={`font-default text-xs mt-4 uppercase tracking-widest transition-all duration-300 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        color: "var(--muted-foreground)",
        transitionDelay: "500ms"
      }} onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.color = "var(--primary)";
      }} onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.color = "var(--muted-foreground)";
      }}>
          Schedule Inspection
        </Link>
      </div>
    </section>;
}